import { useState, useEffect, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  ArrowLeft, Plus, Edit2, Trash2, Save, X, Search,
  ChevronRight, ChevronDown, Cpu, FolderTree,
  ClipboardCheck, AlertTriangle, FileText, Upload,
  FileDown, FileWarning, Send, Activity, Printer, Camera,
} from 'lucide-react'
import { projectApi } from '../services/projectApi'
import { systemApi } from '../services/systemApi'
import { equipmentApi } from '../services/equipmentApi'
import { checklistApi } from '../services/checklistApi'
import { issueApi } from '../services/issueApi'
import { submittalApi } from '../services/submittalApi'
import { dashboardApi } from '../services/dashboardApi'
import { documentApi } from '../services/documentApi'
import { generateProjectReport } from '../services/reportGenerator'
import { exportToCsv } from '../services/csvExport'
import { CsvImportDialog, type ImportField } from '../components/common/CsvImportDialog'
import { LoadingSpinner } from '../components/common/LoadingSpinner'
import { EmptyState } from '../components/common/EmptyState'
import { StatusBadge } from '../components/common/StatusBadge'
import { PriorityBadge } from '../components/common/PriorityBadge'
import { ProgressBar } from '../components/common/ProgressBar'
import { ConfirmDialog } from '../components/common/ConfirmDialog'
import type {
  Project, ProjectUpdate, ProjectStatus,
  System, SystemCreate,
  Equipment, EquipmentCreate, EquipmentStatus,
  Checklist, ChecklistTemplate,
  PunchItemPriority,
  Issue, IssueCreate, IssueUpdate, IssueStatus, IssueCategory, IssueType, IssuePhase, IssuePhoto,
  Submittal, SubmittalCreate, SubmittalUpdate, SubmittalStatus, SubmittalType,
  ActivityLogEntry,
  Document as DocType,
} from '../types'

type TabKey = 'overview' | 'systems' | 'equipment' | 'checklists' | 'issues' | 'submittals' | 'documents'

export function ProjectDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [project, setProject] = useState<Project | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<TabKey>('overview')
  const [editing, setEditing] = useState(false)
  const [editData, setEditData] = useState<ProjectUpdate>({})
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [generatingReport, setGeneratingReport] = useState(false)

  // Systems state
  const [systems, setSystems] = useState<System[]>([])
  const [expandedSystems, setExpandedSystems] = useState<Set<string>>(new Set())
  const [selectedSystem, setSelectedSystem] = useState<System | null>(null)
  const [showSystemModal, setShowSystemModal] = useState(false)
  const [systemForm, setSystemForm] = useState<Partial<SystemCreate>>({})
  const [editingSystemId, setEditingSystemId] = useState<string | null>(null)

  // Equipment state
  const [equipmentList, setEquipmentList] = useState<Equipment[]>([])
  const [showEquipModal, setShowEquipModal] = useState(false)
  const [equipForm, setEquipForm] = useState<Partial<EquipmentCreate>>({})
  const [editingEquipId, setEditingEquipId] = useState<string | null>(null)
  const [showEquipImport, setShowEquipImport] = useState(false)
  const [equipSearch, setEquipSearch] = useState('')
  const [equipStatusFilter, setEquipStatusFilter] = useState('')

  const filteredEquipment = useMemo(() => {
    return equipmentList.filter(eq => {
      if (equipStatusFilter && eq.status !== equipStatusFilter) return false
      if (equipSearch) {
        const term = equipSearch.toLowerCase()
        return eq.tag.toLowerCase().includes(term) || eq.name.toLowerCase().includes(term) ||
               (eq.manufacturer || '').toLowerCase().includes(term) || (eq.model || '').toLowerCase().includes(term)
      }
      return true
    })
  }, [equipmentList, equipSearch, equipStatusFilter])

  // Checklists state
  const [checklists, setChecklists] = useState<Checklist[]>([])
  const [templates, setTemplates] = useState<ChecklistTemplate[]>([])
  const [showChecklistModal, setShowChecklistModal] = useState(false)
  const [checklistForm, setChecklistForm] = useState({ templateId: '', systemId: '', equipmentId: '', assignedTo: '' })
  const [checklistEquipOptions, setChecklistEquipOptions] = useState<Equipment[]>([])
  const [bulkEquipIds, setBulkEquipIds] = useState<Set<string>>(new Set())
  const [bulkMode, setBulkMode] = useState(false)
  const [checklistSearch, setChecklistSearch] = useState('')
  const [checklistStatusFilter, setChecklistStatusFilter] = useState('')
  const [checklistTypeFilter, setChecklistTypeFilter] = useState('')

  const filteredChecklists = useMemo(() => {
    return checklists.filter(cl => {
      if (checklistStatusFilter && cl.status !== checklistStatusFilter) return false
      if (checklistTypeFilter && (cl.type || 'PFC') !== checklistTypeFilter) return false
      if (checklistSearch) {
        const term = checklistSearch.toLowerCase()
        return (cl.name || '').toLowerCase().includes(term) ||
               (cl.templateName || '').toLowerCase().includes(term) ||
               (cl.assignedTo || '').toLowerCase().includes(term)
      }
      return true
    })
  }, [checklists, checklistSearch, checklistStatusFilter, checklistTypeFilter])

  // Issues state
  const [issues, setIssues] = useState<Issue[]>([])
  const [showIssueModal, setShowIssueModal] = useState(false)
  const [editingIssue, setEditingIssue] = useState<Issue | null>(null)
  const [issueForm, setIssueForm] = useState<Partial<IssueCreate>>({})
  const [issueStatus, setIssueStatus] = useState<IssueStatus>('Open')
  const [confirmDeleteIssue, setConfirmDeleteIssue] = useState<Issue | null>(null)
  const [issueResolution, setIssueResolution] = useState('')
  const [issuePhotoFiles, setIssuePhotoFiles] = useState<File[]>([])
  const [issuePhotos, setIssuePhotos] = useState<IssuePhoto[]>([])
  const [issuePhotosLoading, setIssuePhotosLoading] = useState(false)
  const [issueLightboxPhoto, setIssueLightboxPhoto] = useState<IssuePhoto | null>(null)

  // Submittals state
  const [submittals, setSubmittals] = useState<Submittal[]>([])
  const [showSubmittalModal, setShowSubmittalModal] = useState(false)
  const [editingSubmittal, setEditingSubmittal] = useState<Submittal | null>(null)
  const [submittalForm, setSubmittalForm] = useState<Partial<SubmittalCreate>>({})
  const [confirmDeleteSubmittal, setConfirmDeleteSubmittal] = useState<Submittal | null>(null)

  // Activity state
  const [recentActivity, setRecentActivity] = useState<ActivityLogEntry[]>([])
  const [showAllActivity, setShowAllActivity] = useState(false)

  // Overview checklists (for progress display)
  const [overviewChecklists, setOverviewChecklists] = useState<Checklist[]>([])

  // Documents state
  const [documents, setDocuments] = useState<DocType[]>([])
  const [dragOver, setDragOver] = useState(false)

  const [saving, setSaving] = useState(false)

  const loadProject = useCallback(async () => {
    if (!id) return
    try {
      setLoading(true)
      setError(null)
      const data = await projectApi.getById(id)
      setProject(data)
      setEditData({
        name: data.name,
        client: data.client,
        description: data.description,
        status: data.status,
        location: data.location,
        startDate: data.startDate,
        endDate: data.endDate,
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load project')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => { loadProject(); loadIssues(); loadSubmittals(); loadActivity(); loadChecklistsForOverview(); loadSystems() }, [loadProject]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!id) return
    if (activeTab === 'systems' || activeTab === 'equipment') loadSystems()
    if (activeTab === 'checklists') { loadChecklists(); loadTemplates() }
    if (activeTab === 'issues') loadIssues()
    if (activeTab === 'submittals') loadSubmittals()
    if (activeTab === 'documents') loadDocuments()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, id])

  async function loadSystems() {
    if (!id) return
    try {
      const data = await systemApi.getTree(id)
      setSystems(data)
    } catch { /* handled by empty state */ }
  }

  async function loadChecklists() {
    if (!id) return
    try {
      const data = await checklistApi.getByProject(id)
      setChecklists(data)
    } catch { /* handled by empty state */ }
  }

  async function loadTemplates() {
    try {
      const data = await checklistApi.getTemplates(id)
      setTemplates(data)
    } catch { /* handled by empty state */ }
  }

  async function loadIssues() {
    if (!id) return
    try {
      const data = await issueApi.getByProject(id)
      setIssues(data)
    } catch { /* handled by empty state */ }
  }

  async function loadSubmittals() {
    if (!id) return
    try {
      const data = await submittalApi.getByProject(id)
      setSubmittals(data)
    } catch { /* handled by empty state */ }
  }

  async function loadActivity() {
    if (!id) return
    try {
      const data = await dashboardApi.getProjectDashboard(id)
      setRecentActivity(data.recentActivity || [])
    } catch { /* non-critical */ }
  }

  async function loadChecklistsForOverview() {
    if (!id) return
    try {
      const data = await checklistApi.getByProject(id)
      setOverviewChecklists(data)
    } catch { /* non-critical */ }
  }

  async function loadDocuments() {
    if (!id) return
    try {
      const data = await documentApi.getByProject(id)
      setDocuments(data)
    } catch { /* handled by empty state */ }
  }

  async function loadEquipment(systemId: string) {
    setEquipSearch('')
    setEquipStatusFilter('')
    try {
      const data = await equipmentApi.getBySystem(systemId)
      setEquipmentList(data)
    } catch { /* handled by empty state */ }
  }

  async function handleSaveProject(e: React.FormEvent) {
    e.preventDefault()
    if (!id) return
    try {
      setSaving(true)
      const updated = await projectApi.update(id, editData)
      setProject(updated)
      setEditing(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update project')
    } finally {
      setSaving(false)
    }
  }

  async function handleDeleteProject() {
    if (!id) return
    try {
      await projectApi.delete(id)
      navigate('/projects')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete project')
    }
    setConfirmDelete(false)
  }

  async function handleGenerateReport() {
    if (!id) return
    try {
      setGeneratingReport(true)
      setError(null)
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      const apiBase = import.meta.env.VITE_API_URL || '/api'
      // Get auth token if available
      const { getAuthHeadersForStream } = await import('../services/api')
      const authHeaders = await getAuthHeadersForStream()
      Object.assign(headers, authHeaders)

      const response = await fetch(`${apiBase}/ai/cx-report`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ projectId: Number(id) }),
      })

      if (!response.ok) {
        const errText = await response.text()
        throw new Error(`Report generation failed: ${errText}`)
      }

      const contentType = response.headers.get('Content-Type') || ''
      if (contentType.includes('json')) {
        // Fallback: markdown returned as JSON
        const data = await response.json() as { markdown: string; error?: string }
        const blob = new Blob([data.markdown], { type: 'text/markdown' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `Cx_Report_${project?.projectNumber || id}.md`
        a.click()
        URL.revokeObjectURL(url)
      } else {
        // Binary docx
        const blob = await response.blob()
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        const disposition = response.headers.get('Content-Disposition') || ''
        const match = disposition.match(/filename="?([^"]+)"?/)
        a.download = match?.[1] || `Cx_Report_${project?.projectNumber || id}.docx`
        a.click()
        URL.revokeObjectURL(url)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate report')
    } finally {
      setGeneratingReport(false)
    }
  }

  function toggleExpand(systemId: string) {
    setExpandedSystems((prev) => {
      const next = new Set(prev)
      if (next.has(systemId)) next.delete(systemId)
      else next.add(systemId)
      return next
    })
  }

  function selectSystem(system: System) {
    setSelectedSystem(system)
    loadEquipment(system.id)
    if (activeTab !== 'equipment') setActiveTab('equipment')
  }

  async function handleCreateSystem(e: React.FormEvent) {
    e.preventDefault()
    if (!id) return
    try {
      setSaving(true)
      await systemApi.create({ ...systemForm, projectId: id } as SystemCreate)
      setShowSystemModal(false)
      setSystemForm({})
      setEditingSystemId(null)
      loadSystems()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save system')
    } finally {
      setSaving(false)
    }
  }

  async function handleUpdateSystem(e: React.FormEvent) {
    e.preventDefault()
    if (!editingSystemId) return
    try {
      setSaving(true)
      await systemApi.update(editingSystemId, systemForm)
      setShowSystemModal(false)
      setSystemForm({})
      setEditingSystemId(null)
      loadSystems()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update system')
    } finally {
      setSaving(false)
    }
  }

  async function handleDeleteSystem(systemId: string) {
    try {
      await systemApi.delete(systemId)
      if (selectedSystem?.id === systemId) {
        setSelectedSystem(null)
        setEquipmentList([])
      }
      loadSystems()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete system')
    }
  }

  async function handleCreateEquipment(e: React.FormEvent) {
    e.preventDefault()
    if (!selectedSystem) return
    try {
      setSaving(true)
      await equipmentApi.create({ ...equipForm, systemId: selectedSystem.id } as EquipmentCreate)
      setShowEquipModal(false)
      setEquipForm({})
      setEditingEquipId(null)
      loadEquipment(selectedSystem.id)
      loadSystems()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save equipment')
    } finally {
      setSaving(false)
    }
  }

  async function handleUpdateEquipment(e: React.FormEvent) {
    e.preventDefault()
    if (!editingEquipId || !selectedSystem) return
    try {
      setSaving(true)
      await equipmentApi.update(editingEquipId, equipForm)
      setShowEquipModal(false)
      setEquipForm({})
      setEditingEquipId(null)
      loadEquipment(selectedSystem.id)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update equipment')
    } finally {
      setSaving(false)
    }
  }

  async function handleDeleteEquipment(equipId: string) {
    if (!selectedSystem) return
    try {
      await equipmentApi.delete(equipId)
      loadEquipment(selectedSystem.id)
      loadSystems()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete equipment')
    }
  }

  const equipImportFields: ImportField[] = [
    { key: 'tag', label: 'Tag', required: true },
    { key: 'name', label: 'Name', required: true },
    { key: 'description', label: 'Description' },
    { key: 'manufacturer', label: 'Manufacturer' },
    { key: 'model', label: 'Model' },
    { key: 'serialNumber', label: 'Serial Number' },
    { key: 'location', label: 'Location' },
    { key: 'status', label: 'Status', options: [
      { value: 'Pending', label: 'Pending' }, { value: 'Installed', label: 'Installed' },
      { value: 'Tested', label: 'Tested' }, { value: 'Commissioned', label: 'Commissioned' },
      { value: 'Deficient', label: 'Deficient' },
    ]},
  ]

  async function handleEquipImport(rows: Record<string, string>[]) {
    if (!selectedSystem) return { imported: 0, errors: ['No system selected'] }
    let imported = 0
    const errors: string[] = []
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i]!
      try {
        await equipmentApi.create({
          systemId: selectedSystem.id,
          tag: row.tag ?? '',
          name: row.name ?? '',
          description: row.description || '',
          manufacturer: row.manufacturer || '',
          model: row.model || '',
          serialNumber: row.serialNumber || '',
          location: row.location || '',
          status: (row.status as EquipmentStatus) || 'Pending',
        })
        imported++
      } catch (err) {
        errors.push(`Row ${i + 1}: ${err instanceof Error ? err.message : 'Failed'}`)
      }
    }
    loadEquipment(selectedSystem.id)
    loadSystems()
    return { imported, errors }
  }

  async function handleCreateChecklist(e: React.FormEvent) {
    e.preventDefault()
    if (!id) return
    try {
      setSaving(true)
      if (bulkMode && bulkEquipIds.size > 0) {
        // Bulk create: one checklist per selected equipment
        const errors: string[] = []
        for (const eqId of bulkEquipIds) {
          try {
            await checklistApi.create({
              projectId: id,
              templateId: checklistForm.templateId,
              systemId: checklistForm.systemId || undefined,
              equipmentId: eqId,
              assignedTo: checklistForm.assignedTo || undefined,
            })
          } catch (err) {
            const eq = checklistEquipOptions.find(e => e.id === eqId)
            errors.push(`${eq?.tag || eqId}: ${err instanceof Error ? err.message : 'Failed'}`)
          }
        }
        if (errors.length > 0) setError(`Some checklists failed: ${errors.join('; ')}`)
      } else {
        await checklistApi.create({
          projectId: id,
          templateId: checklistForm.templateId,
          systemId: checklistForm.systemId || undefined,
          equipmentId: checklistForm.equipmentId || undefined,
          assignedTo: checklistForm.assignedTo || undefined,
        })
      }
      setShowChecklistModal(false)
      setChecklistForm({ templateId: '', systemId: '', equipmentId: '', assignedTo: '' })
      setBulkEquipIds(new Set())
      setBulkMode(false)
      loadChecklists()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create checklist')
    } finally {
      setSaving(false)
    }
  }

  // Issue CRUD functions
  function openEditIssue(issue: Issue) {
    setEditingIssue(issue)
    setIssueForm({
      title: issue.title,
      description: issue.description || '',
      category: issue.category,
      type: issue.type,
      priority: issue.priority,
      phase: issue.phase || undefined,
      responsibleParty: issue.responsibleParty || '',
      assignedTo: issue.assignedTo || '',
      dueDate: issue.dueDate ? issue.dueDate.split('T')[0] : '',
      systemId: issue.systemId ?? undefined,
    })
    setIssueStatus(issue.status)
    setIssueResolution(issue.resolution || '')
    setIssuePhotoFiles([])
    setIssuePhotos([])
    setShowIssueModal(true)

    // Load photos
    setIssuePhotosLoading(true)
    issueApi.getPhotos(String(issue.id)).then(setIssuePhotos).catch(() => {}).finally(() => setIssuePhotosLoading(false))
  }

  async function handleDeleteIssuePhoto(photoId: number) {
    if (!editingIssue) return
    try {
      await issueApi.deletePhoto(String(editingIssue.id), String(photoId))
      setIssuePhotos(prev => prev.filter(p => p.id !== photoId))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete photo')
    }
  }

  async function handleCreateIssue(e: React.FormEvent) {
    e.preventDefault()
    if (!id) return
    try {
      setSaving(true)
      let savedIssue: Issue
      if (editingIssue) {
        const updateData: IssueUpdate = {
          title: issueForm.title,
          description: issueForm.description,
          category: issueForm.category,
          type: issueForm.type,
          priority: issueForm.priority,
          phase: issueForm.phase || null,
          responsibleParty: issueForm.responsibleParty || null,
          assignedTo: issueForm.assignedTo || null,
          dueDate: issueForm.dueDate || null,
          status: issueStatus,
          resolution: issueResolution || null,
        }
        savedIssue = await issueApi.update(String(editingIssue.id), updateData)
      } else {
        savedIssue = await issueApi.create({ ...issueForm, projectId: Number(id) } as IssueCreate)
      }

      // Upload any staged photos
      for (const file of issuePhotoFiles) {
        await issueApi.uploadPhoto(String(savedIssue.id), file)
      }

      setShowIssueModal(false)
      setIssueForm({})
      setEditingIssue(null)
      setIssuePhotoFiles([])
      setIssuePhotos([])
      loadIssues()
    } catch (err) {
      setError(err instanceof Error ? err.message : editingIssue ? 'Failed to update issue' : 'Failed to create issue')
    } finally {
      setSaving(false)
    }
  }

  async function handleDeleteIssue() {
    if (!confirmDeleteIssue) return
    try {
      await issueApi.delete(String(confirmDeleteIssue.id))
      setConfirmDeleteIssue(null)
      loadIssues()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete issue')
    }
  }

  async function handleQuickIssueStatusChange(issue: Issue) {
    const statusOrder: IssueStatus[] = ['Open', 'InProgress', 'PendingResponse', 'Resolved', 'Closed']
    const currentIndex = statusOrder.indexOf(issue.status)
    const nextStatus = statusOrder[(currentIndex + 1) % statusOrder.length]
    try {
      await issueApi.update(String(issue.id), { status: nextStatus })
      loadIssues()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status')
    }
  }

  // Submittal handlers
  async function handleCreateSubmittal(e: React.FormEvent) {
    e.preventDefault()
    if (!id) return
    try {
      setSaving(true)
      if (editingSubmittal) {
        const updateData: SubmittalUpdate = {
          title: submittalForm.title,
          description: submittalForm.description,
          type: submittalForm.type,
          submittedBy: submittalForm.submittedBy,
          dueDate: submittalForm.dueDate,
        }
        if ((submittalForm as any).status) {
          updateData.status = (submittalForm as any).status
        }
        await submittalApi.update(String(editingSubmittal.id), updateData)
      } else {
        const num = `SUB-${String(submittals.length + 1).padStart(3, '0')}`
        await submittalApi.create({
          ...submittalForm,
          projectId: Number(id),
          submittalNumber: submittalForm.submittalNumber || num,
          title: submittalForm.title || '',
          type: submittalForm.type || 'Other',
        } as SubmittalCreate)
      }
      setShowSubmittalModal(false)
      setSubmittalForm({})
      setEditingSubmittal(null)
      loadSubmittals()
    } catch (err) {
      setError(err instanceof Error ? err.message : editingSubmittal ? 'Failed to update submittal' : 'Failed to create submittal')
    } finally {
      setSaving(false)
    }
  }

  function openEditSubmittal(sub: Submittal) {
    setEditingSubmittal(sub)
    setSubmittalForm({
      title: sub.title,
      description: sub.description || '',
      type: sub.type,
      submittedBy: sub.submittedBy || '',
      dueDate: sub.dueDate ? sub.dueDate.split('T')[0] : '',
      submittalNumber: sub.submittalNumber,
      status: sub.status as any,
    } as any)
    setShowSubmittalModal(true)
  }

  async function handleDeleteSubmittal() {
    if (!confirmDeleteSubmittal) return
    try {
      await submittalApi.delete(String(confirmDeleteSubmittal.id))
      setConfirmDeleteSubmittal(null)
      loadSubmittals()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete submittal')
    }
  }

  async function handleQuickSubmittalStatus(sub: Submittal) {
    const statusOrder: SubmittalStatus[] = ['Pending', 'Under_Review', 'Approved']
    const currentIndex = statusOrder.indexOf(sub.status)
    const nextStatus = statusOrder[(currentIndex + 1) % statusOrder.length]
    try {
      await submittalApi.update(String(sub.id), { status: nextStatus })
      loadSubmittals()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update submittal status')
    }
  }

  async function handleFileUpload(files: FileList | null) {
    if (!files || !id) return
    for (const file of Array.from(files)) {
      try {
        await documentApi.upload(id, file)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to upload file')
      }
    }
    loadDocuments()
  }

  async function handleDeleteDocument(docId: string) {
    try {
      await documentApi.delete(docId)
      loadDocuments()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete document')
    }
  }

  function renderSystemTree(nodes: System[], depth = 0): React.ReactNode {
    return nodes.map((node) => {
      const isExpanded = expandedSystems.has(node.id)
      const hasChildren = node.children && node.children.length > 0
      const isSelected = selectedSystem?.id === node.id
      return (
        <div key={node.id}>
          <div
            className={`system-node${isSelected ? ' selected' : ''}`}
            style={{ paddingLeft: `${depth * 24 + 12}px` }}
          >
            <button
              className="system-expand-btn"
              onClick={() => hasChildren && toggleExpand(node.id)}
              style={{ visibility: hasChildren ? 'visible' : 'hidden' }}
            >
              {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
            <div className="system-node-content" onClick={() => selectSystem(node)}>
              <FolderTree size={16} className="text-accent" />
              <span className="system-code">{node.code}</span>
              <span className="system-name">{node.name}</span>
              <StatusBadge status={node.status} />
              <span className="badge badge-gray-light">{node.equipmentCount} equip</span>
            </div>
            <div className="system-node-actions">
              <button
                className="btn btn-ghost btn-xs btn-icon"
                onClick={(e) => {
                  e.stopPropagation()
                  setSystemForm({ parentSystemId: node.id, code: '', name: '', description: '' })
                  setEditingSystemId(null)
                  setShowSystemModal(true)
                }}
                title="Add child system"
              >
                <Plus size={14} />
              </button>
              <button
                className="btn btn-ghost btn-xs btn-icon"
                onClick={(e) => {
                  e.stopPropagation()
                  setSystemForm({ code: node.code, name: node.name, description: node.description })
                  setEditingSystemId(node.id)
                  setShowSystemModal(true)
                }}
                title="Edit system"
              >
                <Edit2 size={14} />
              </button>
              <button
                className="btn btn-ghost btn-xs btn-icon text-danger"
                onClick={(e) => { e.stopPropagation(); handleDeleteSystem(node.id) }}
                title="Delete system"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
          {hasChildren && isExpanded && (
            <div className="system-children">
              {renderSystemTree(node.children, depth + 1)}
            </div>
          )}
        </div>
      )
    })
  }

  if (loading) return <LoadingSpinner message="Loading project..." />
  if (error && !project) {
    return (
      <div className="page-container">
        <div className="info-card danger">
          <p style={{ margin: 0 }}>{error}</p>
          <button className="btn btn-sm btn-secondary mt-3" onClick={() => navigate('/projects')}>Back to Projects</button>
        </div>
      </div>
    )
  }
  if (!project) return null

  const tabs: { key: TabKey; label: string; icon: React.ReactNode }[] = [
    { key: 'overview', label: 'Overview', icon: null },
    { key: 'systems', label: 'Systems', icon: <FolderTree size={14} /> },
    { key: 'equipment', label: 'Equipment', icon: <Cpu size={14} /> },
    { key: 'checklists', label: 'Checklists', icon: <ClipboardCheck size={14} /> },
    { key: 'issues', label: 'Issues', icon: <FileWarning size={14} /> },
    { key: 'submittals', label: 'Submittals', icon: <Send size={14} /> },
    { key: 'documents', label: 'Documents', icon: <FileText size={14} /> },
  ]

  function flattenSystems(nodes: System[]): System[] {
    const flat: System[] = []
    for (const n of nodes) {
      flat.push(n)
      if (n.children) flat.push(...flattenSystems(n.children))
    }
    return flat
  }

  const allSystems = flattenSystems(systems)

  return (
    <div className="page-container animate-fade-in">
      <div className="breadcrumbs">
        <Link to="/projects">Projects</Link>
        <span className="breadcrumbs-separator">/</span>
        <span className="breadcrumbs-current">{project.name}</span>
      </div>

      <div className="page-header">
        <div className="d-flex align-center gap-3">
          <button className="btn btn-ghost btn-icon" onClick={() => navigate('/projects')}>
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1>{project.name}</h1>
            <p className="page-subtitle">{project.projectNumber} - {project.client}</p>
          </div>
          <StatusBadge status={project.status} />
        </div>
        <div className="page-header-actions">
          {!editing && (
            <>
              <button className="btn btn-secondary" onClick={() => setEditing(true)}>
                <Edit2 size={16} /> Edit
              </button>
              <button className="btn btn-outline-danger" onClick={() => setConfirmDelete(true)}>
                <Trash2 size={16} /> Delete
              </button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="info-card danger mb-4">
          <p style={{ margin: 0, color: 'var(--accent-red)' }}>{error}</p>
        </div>
      )}

      <div className="tabs mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            className={`tab${activeTab === tab.key ? ' active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div>
          {editing ? (
            <form onSubmit={handleSaveProject}>
              <div className="card">
                <div className="card-header">
                  <h3>Edit Project</h3>
                </div>
                <div className="card-body">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Name</label>
                      <input className="form-input" value={editData.name || ''} onChange={(e) => setEditData({ ...editData, name: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label>Client</label>
                      <input className="form-input" value={editData.client || ''} onChange={(e) => setEditData({ ...editData, client: e.target.value })} />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Location</label>
                      <input className="form-input" value={editData.location || ''} onChange={(e) => setEditData({ ...editData, location: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label>Status</label>
                      <select className="form-select" value={editData.status || ''} onChange={(e) => setEditData({ ...editData, status: e.target.value as ProjectStatus })}>
                        <option value="Draft">Draft</option>
                        <option value="Active">Active</option>
                        <option value="OnHold">On Hold</option>
                        <option value="Completed">Completed</option>
                        <option value="Cancelled">Cancelled</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <textarea className="form-textarea" value={editData.description || ''} onChange={(e) => setEditData({ ...editData, description: e.target.value })} rows={3} />
                  </div>
                </div>
                <div className="card-footer d-flex justify-end gap-3">
                  <button type="button" className="btn btn-secondary" onClick={() => setEditing(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    <Save size={16} /> {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </form>
          ) : (
            <>
              <div className="metrics-row mb-6">
                <div className="metric-card blue">
                  <div className="metric-header"><span className="metric-label">Systems</span></div>
                  <div className="metric-value">{project.systemCount}</div>
                </div>
                <div className="metric-card cyan">
                  <div className="metric-header"><span className="metric-label">Equipment</span></div>
                  <div className="metric-value">{project.equipmentCount}</div>
                </div>
                <div className="metric-card green">
                  <div className="metric-header"><span className="metric-label">Completion</span></div>
                  <div className="metric-value">{Math.round(project.completionPercentage)}%</div>
                </div>
                <div className="metric-card amber" style={{ cursor: 'pointer' }} onClick={() => setActiveTab('issues')}>
                  <div className="metric-header"><span className="metric-label">Open Issues</span></div>
                  <div className="metric-value">{issues.filter(i => i.status === 'Open' || i.status === 'InProgress' || i.status === 'PendingResponse').length}</div>
                  <div className="metric-subtext">{issues.length} total</div>
                </div>
              </div>
              <div className="card">
                <div className="card-header"><h3>Project Details</h3></div>
                <div className="card-body">
                  <div className="detail-grid">
                    <div className="detail-row"><span className="detail-label">Description</span><span className="detail-value">{project.description || 'No description'}</span></div>
                    <div className="detail-row"><span className="detail-label">Location</span><span className="detail-value">{project.location || 'Not specified'}</span></div>
                    <div className="detail-row"><span className="detail-label">Start Date</span><span className="detail-value">{new Date(project.startDate).toLocaleDateString()}</span></div>
                    <div className="detail-row"><span className="detail-label">End Date</span><span className="detail-value">{project.endDate ? new Date(project.endDate).toLocaleDateString() : 'Not set'}</span></div>
                    <div className="detail-row"><span className="detail-label">Created</span><span className="detail-value">{new Date(project.createdAt).toLocaleString()}</span></div>
                  </div>
                </div>
              </div>
              <div className="card mt-4">
                <div className="card-header">
                  <h3>Commissioning Report</h3>
                </div>
                <div className="card-body">
                  <p className="text-secondary-color text-sm mb-3">
                    Generate a formal Cx Report summarizing all commissioning activities, test results, deficiencies, and recommendations for this project.
                  </p>
                  <div className="d-flex gap-3">
                    <Link to="/cx-report" className="btn btn-primary">
                      <FileDown size={16} /> Open Cx Report Generator
                    </Link>
                    <button
                      className="btn btn-secondary"
                      onClick={handleGenerateReport}
                      disabled={generatingReport}
                    >
                      <FileDown size={16} /> {generatingReport ? 'Generating...' : 'Quick Download'}
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={() => id && generateProjectReport(id)}
                    >
                      <Printer size={16} /> Print Report
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        const date = new Date().toISOString().split('T')[0]
                        const prefix = project?.projectNumber || 'export'

                        if (overviewChecklists.length > 0) {
                          exportToCsv(`${prefix}_checklists_${date}.csv`,
                            ['Name', 'Type', 'Template', 'Status', 'Completion %', 'Assigned To'],
                            overviewChecklists.map(cl => [cl.name, cl.type || 'PFC', cl.templateName || '', cl.status, String(cl.completionPercentage), cl.assignedTo || ''])
                          )
                        }

                        if (issues.length > 0) {
                          exportToCsv(`${prefix}_issues_${date}.csv`,
                            ['Number', 'Title', 'Type', 'Category', 'Priority', 'Status', 'Phase', 'Assigned To', 'Due Date', 'Resolution'],
                            issues.map(i => [i.issueNumber, i.title, i.type, i.category, i.priority, i.status, i.phase || '', i.assignedTo || '', i.dueDate || '', i.resolution || ''])
                          )
                        }

                        if (submittals.length > 0) {
                          exportToCsv(`${prefix}_submittals_${date}.csv`,
                            ['Number', 'Title', 'Type', 'Status', 'Submitted By', 'Due Date'],
                            submittals.map(s => [s.submittalNumber, s.title, s.type.replace(/_/g, ' '), s.status.replace(/_/g, ' '), s.submittedBy || '', s.dueDate || ''])
                          )
                        }
                      }}
                    >
                      <FileDown size={16} /> Export All Data
                    </button>
                  </div>
                </div>
              </div>

              {overviewChecklists.length > 0 && (() => {
                // Flatten systems for lookup
                const flatSystems: System[] = []
                function collectSystems(list: System[]) {
                  for (const s of list) { flatSystems.push(s); if (s.children) collectSystems(s.children) }
                }
                collectSystems(systems)
                const sysMap = new Map(flatSystems.map(s => [String(s.id), s]))

                // Group by system
                const bySystem = overviewChecklists.reduce((acc, cl) => {
                  const sys = cl.systemId ? sysMap.get(String(cl.systemId)) : null
                  const key = sys ? `${sys.code} — ${sys.name}` : 'Unassigned'
                  if (!acc[key]) acc[key] = { total: 0, completed: 0, count: 0 }
                  acc[key].total += cl.totalItems
                  acc[key].completed += cl.completedItems
                  acc[key].count++
                  return acc
                }, {} as Record<string, { total: number; completed: number; count: number }>)

                // Group by template
                const byTemplate = overviewChecklists.reduce((acc, cl) => {
                  const key = cl.templateName || 'Untitled'
                  if (!acc[key]) acc[key] = { total: 0, completed: 0, count: 0 }
                  acc[key].total += cl.totalItems
                  acc[key].completed += cl.completedItems
                  acc[key].count++
                  return acc
                }, {} as Record<string, { total: number; completed: number; count: number }>)

                const overallCompleted = overviewChecklists.reduce((s, c) => s + c.completedItems, 0)
                const overallTotal = overviewChecklists.reduce((s, c) => s + c.totalItems, 0)
                const overallPct = overallTotal > 0 ? Math.round((overallCompleted / overallTotal) * 100) : 0

                const systemEntries = Object.entries(bySystem).sort(([, a], [, b]) => {
                  const pctA = a.total > 0 ? a.completed / a.total : 0
                  const pctB = b.total > 0 ? b.completed / b.total : 0
                  return pctA - pctB
                })

                return (
                  <div className="card mt-4">
                    <div className="card-header" style={{ cursor: 'pointer' }} onClick={() => setActiveTab('checklists')}>
                      <h3>Checklist Progress</h3>
                      <span className="text-sm text-secondary-color">{overviewChecklists.length} checklist{overviewChecklists.length !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="card-body">
                      <div className="mb-4">
                        <ProgressBar value={overallPct} />
                      </div>

                      {systemEntries.length > 1 && (
                        <>
                          <div className="text-xs text-muted font-medium mb-2" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>By System</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-3)', marginBottom: 'var(--spacing-4)' }}>
                            {systemEntries.map(([name, data]) => {
                              const pct = data.total > 0 ? Math.round((data.completed / data.total) * 100) : 0
                              return (
                                <div key={name}>
                                  <div className="d-flex justify-between align-center mb-1">
                                    <span className="text-sm font-medium">{name}</span>
                                    <span className="text-xs text-muted">{data.completed}/{data.total} items ({data.count})</span>
                                  </div>
                                  <ProgressBar value={pct} size="sm" showLabel={false} />
                                </div>
                              )
                            })}
                          </div>
                        </>
                      )}

                      <div className="text-xs text-muted font-medium mb-2" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>By Template</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-3)' }}>
                        {Object.entries(byTemplate).map(([name, data]) => {
                          const pct = data.total > 0 ? Math.round((data.completed / data.total) * 100) : 0
                          return (
                            <div key={name}>
                              <div className="d-flex justify-between align-center mb-1">
                                <span className="text-sm font-medium">{name}</span>
                                <span className="text-xs text-muted">{data.completed}/{data.total} items ({data.count})</span>
                              </div>
                              <ProgressBar value={pct} size="sm" showLabel={false} />
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                )
              })()}

              <div className="card mt-4">
                <div className="card-header">
                  <h3>Recent Activity</h3>
                  <Activity size={20} className="text-muted" />
                </div>
                <div className="card-body">
                  {recentActivity.length === 0 ? (
                    <p className="text-muted text-center" style={{ padding: 'var(--spacing-4) 0' }}>
                      No recent activity for this project
                    </p>
                  ) : (() => {
                    const entityIcon = (type: string) => {
                      const iconStyle = (color: string) => ({
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: 28, height: 28, borderRadius: '50%',
                        backgroundColor: `color-mix(in srgb, ${color} 15%, transparent)`,
                        color, flexShrink: 0,
                      })
                      switch (type) {
                        case 'Equipment': return <span style={iconStyle('var(--accent-blue, var(--ise-accent))')}><Cpu size={14} /></span>
                        case 'Checklist': return <span style={iconStyle('var(--accent-green, #22c55e)')}><ClipboardCheck size={14} /></span>
                        case 'PunchItem': return <span style={iconStyle('var(--accent-red, #ef4444)')}><AlertTriangle size={14} /></span>
                        case 'System': return <span style={iconStyle('var(--accent-purple, #a855f7)')}><FolderTree size={14} /></span>
                        case 'Submittal': return <span style={iconStyle('var(--accent-orange, #f59e0b)')}><Send size={14} /></span>
                        case 'Project': return <span style={iconStyle('var(--accent-blue, var(--ise-accent))')}><FileText size={14} /></span>
                        default: return <span style={iconStyle('var(--text-secondary)')}><Activity size={14} /></span>
                      }
                    }

                    const actionColor = (action: string) => {
                      const lower = action.toLowerCase()
                      if (lower.includes('created')) return 'var(--accent-green, #22c55e)'
                      if (lower.includes('updated')) return 'var(--accent-blue, var(--ise-accent))'
                      if (lower.includes('deleted')) return 'var(--accent-red, #ef4444)'
                      return 'var(--text-secondary)'
                    }

                    const formatDateLabel = (dateStr: string) => {
                      const date = new Date(dateStr)
                      const now = new Date()
                      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
                      const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1)
                      const entryDate = new Date(date.getFullYear(), date.getMonth(), date.getDate())
                      if (entryDate.getTime() === today.getTime()) return 'Today'
                      if (entryDate.getTime() === yesterday.getTime()) return 'Yesterday'
                      return entryDate.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
                    }

                    const displayedEntries = showAllActivity ? recentActivity : recentActivity.slice(0, 10)

                    const grouped: { label: string; entries: typeof recentActivity }[] = []
                    for (const entry of displayedEntries) {
                      const label = formatDateLabel(entry.performedAt)
                      const last = grouped[grouped.length - 1]
                      if (last && last.label === label) {
                        last.entries.push(entry)
                      } else {
                        grouped.push({ label, entries: [entry] })
                      }
                    }

                    const renderEntityLink = (entry: ActivityLogEntry) => {
                      const desc = entry.description || entry.entityName || ''
                      if (entry.entityType === 'Equipment' && entry.entityId) {
                        return <Link to={`/equipment/${entry.entityId}`} style={{ color: 'var(--ise-accent)', textDecoration: 'none' }}>{desc}</Link>
                      }
                      if (entry.entityType === 'Checklist' && entry.entityId) {
                        return <Link to={`/checklists/${entry.entityId}`} style={{ color: 'var(--ise-accent)', textDecoration: 'none' }}>{desc}</Link>
                      }
                      if (entry.entityType === 'System') {
                        return <span style={{ cursor: 'pointer', color: 'var(--ise-accent)' }} onClick={() => setActiveTab('systems')}>{desc}</span>
                      }
                      return <span>{desc}</span>
                    }

                    return (
                      <div className="activity-feed">
                        {grouped.map((group) => (
                          <div key={group.label}>
                            <div style={{
                              fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-secondary)',
                              textTransform: 'uppercase', letterSpacing: '0.05em',
                              padding: 'var(--spacing-2) 0', marginTop: 'var(--spacing-2)',
                              borderBottom: '1px solid var(--border-subtle, var(--ise-border))',
                              marginBottom: 'var(--spacing-2)',
                            }}>
                              {group.label}
                            </div>
                            {group.entries.map((entry) => (
                              <div key={entry.id} className="activity-item" style={{ display: 'flex', gap: 'var(--spacing-3)', alignItems: 'flex-start', padding: 'var(--spacing-2) 0' }}>
                                {entityIcon(entry.entityType)}
                                <div className="activity-content" style={{ flex: 1, minWidth: 0 }}>
                                  <p className="activity-text" style={{ margin: 0 }}>
                                    <span style={{ color: actionColor(entry.action), fontWeight: 600 }}>{entry.action}</span>{' '}
                                    {renderEntityLink(entry)}
                                  </p>
                                  <span className="activity-meta" style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                                    {entry.performedBy} — {new Date(entry.performedAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        ))}
                        {recentActivity.length > 10 && (
                          <div style={{ textAlign: 'center', paddingTop: 'var(--spacing-3)' }}>
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => setShowAllActivity(!showAllActivity)}
                            >
                              {showAllActivity ? 'Show less' : `Show all ${recentActivity.length} entries`}
                            </button>
                          </div>
                        )}
                      </div>
                    )
                  })()}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Systems Tab */}
      {activeTab === 'systems' && (
        <div>
          <div className="d-flex justify-between align-center mb-4">
            <h3>System Hierarchy</h3>
            <button className="btn btn-primary btn-sm" onClick={() => { setSystemForm({ code: '', name: '', description: '' }); setEditingSystemId(null); setShowSystemModal(true) }}>
              <Plus size={14} /> Add System
            </button>
          </div>
          {systems.length === 0 ? (
            <EmptyState
              icon={<FolderTree size={48} />}
              title="No systems"
              message="Add systems to organize equipment for this project."
              action={<button className="btn btn-primary" onClick={() => { setSystemForm({ code: '', name: '', description: '' }); setShowSystemModal(true) }}><Plus size={16} /> Add System</button>}
            />
          ) : (
            <div className="card">
              <div className="card-body p-0">
                <div className="system-tree">
                  {renderSystemTree(systems)}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Equipment Tab */}
      {activeTab === 'equipment' && (
        <div>
          <div className="d-flex justify-between align-center mb-4">
            <div>
              <h3>Equipment</h3>
              {selectedSystem && <p className="text-sm text-muted mt-1">System: {selectedSystem.code} - {selectedSystem.name}</p>}
            </div>
            {selectedSystem && (
              <div className="d-flex gap-2">
                <button className="btn btn-secondary btn-sm" onClick={() => {
                  const date = new Date().toISOString().split('T')[0]
                  exportToCsv(
                    `equipment_${selectedSystem?.code || 'export'}_${date}.csv`,
                    ['Tag', 'Name', 'Manufacturer', 'Model', 'Serial Number', 'Location', 'Status', 'Description'],
                    filteredEquipment.map(eq => [eq.tag, eq.name, eq.manufacturer || '', eq.model || '', eq.serialNumber || '', eq.location || '', eq.status, eq.description || ''])
                  )
                }}>
                  <FileDown size={14} /> Export CSV
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => setShowEquipImport(true)}>
                  <Upload size={14} /> Import CSV
                </button>
                <button className="btn btn-primary btn-sm" onClick={() => { setEquipForm({ tag: '', name: '', description: '' }); setEditingEquipId(null); setShowEquipModal(true) }}>
                  <Plus size={14} /> Add Equipment
                </button>
              </div>
            )}
          </div>
          {!selectedSystem ? (
            <EmptyState icon={<Cpu size={48} />} title="Select a system" message="Choose a system from the Systems tab to view its equipment." />
          ) : equipmentList.length === 0 ? (
            <EmptyState
              icon={<Cpu size={48} />}
              title="No equipment"
              message={`No equipment found for ${selectedSystem.code}.`}
              action={<button className="btn btn-primary" onClick={() => { setEquipForm({ tag: '', name: '', description: '' }); setShowEquipModal(true) }}><Plus size={16} /> Add Equipment</button>}
            />
          ) : (
            <>
            <div className="d-flex gap-2 align-center mb-3" style={{ flexWrap: 'wrap' }}>
              <div style={{ position: 'relative', flex: '1 1 200px', maxWidth: 300 }}>
                <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', opacity: 0.5 }} />
                <input
                  className="form-input"
                  type="text"
                  placeholder="Search tag, name, manufacturer, model..."
                  value={equipSearch}
                  onChange={e => setEquipSearch(e.target.value)}
                  style={{ paddingLeft: 32 }}
                />
              </div>
              <select
                className="form-select"
                value={equipStatusFilter}
                onChange={e => setEquipStatusFilter(e.target.value)}
                style={{ width: 'auto', minWidth: 140 }}
              >
                <option value="">All Statuses</option>
                <option value="Pending">Pending</option>
                <option value="Installed">Installed</option>
                <option value="Tested">Tested</option>
                <option value="Commissioned">Commissioned</option>
                <option value="Deficient">Deficient</option>
              </select>
              <span className="text-sm text-muted" style={{ whiteSpace: 'nowrap' }}>
                Showing {filteredEquipment.length} of {equipmentList.length}
              </span>
              {(equipSearch || equipStatusFilter) && (
                <button className="btn btn-ghost btn-sm" onClick={() => { setEquipSearch(''); setEquipStatusFilter('') }}>
                  <X size={14} /> Clear
                </button>
              )}
            </div>
            {filteredEquipment.length === 0 ? (
              <EmptyState icon={<Search size={48} />} title="No matches" message="No equipment matches your search or filter criteria." />
            ) : (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr><th>Tag</th><th>Name</th><th>Manufacturer</th><th>Model</th><th>Status</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {filteredEquipment.map((eq) => (
                    <tr key={eq.id}>
                      <td><Link to={`/equipment/${eq.id}`} className="font-medium text-accent">{eq.tag}</Link></td>
                      <td>{eq.name}</td>
                      <td className="text-secondary-color">{eq.manufacturer}</td>
                      <td className="text-secondary-color">{eq.model}</td>
                      <td><StatusBadge status={eq.status} /></td>
                      <td>
                        <div className="table-actions">
                          <button className="btn btn-ghost btn-xs btn-icon" onClick={() => { setEquipForm({ tag: eq.tag, name: eq.name, description: eq.description, manufacturer: eq.manufacturer, model: eq.model, serialNumber: eq.serialNumber, location: eq.location, status: eq.status }); setEditingEquipId(eq.id); setShowEquipModal(true) }}><Edit2 size={14} /></button>
                          <button className="btn btn-ghost btn-xs btn-icon text-danger" onClick={() => handleDeleteEquipment(eq.id)}><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
            </>
          )}
        </div>
      )}

      {/* Checklists Tab */}
      {activeTab === 'checklists' && (
        <div>
          <div className="d-flex justify-between align-center mb-4">
            <h3>Checklists</h3>
            <div className="d-flex gap-2">
              <button className="btn btn-secondary btn-sm" onClick={() => {
                const date = new Date().toISOString().split('T')[0]
                exportToCsv(
                  `checklists_${project?.projectNumber || 'export'}_${date}.csv`,
                  ['Name', 'Type', 'Template', 'Status', 'Completion %', 'Assigned To'],
                  checklists.map(cl => [cl.name, cl.type || 'PFC', cl.templateName || '', cl.status, String(cl.completionPercentage), cl.assignedTo || ''])
                )
              }}>
                <FileDown size={14} /> Export CSV
              </button>
              <button className="btn btn-primary btn-sm" onClick={() => setShowChecklistModal(true)}><Plus size={14} /> Create Checklist</button>
            </div>
          </div>
          {checklists.length === 0 ? (
            <EmptyState icon={<ClipboardCheck size={48} />} title="No checklists" message="Create a checklist from a template to begin testing." action={<button className="btn btn-primary" onClick={() => setShowChecklistModal(true)}><Plus size={16} /> Create Checklist</button>} />
          ) : (
            <>
            <div className="d-flex gap-2 align-center mb-3" style={{ flexWrap: 'wrap' }}>
              <div style={{ position: 'relative', flex: '1 1 200px', maxWidth: 300 }}>
                <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', opacity: 0.5 }} />
                <input
                  className="form-input"
                  type="text"
                  placeholder="Search name, template, assignee..."
                  value={checklistSearch}
                  onChange={e => setChecklistSearch(e.target.value)}
                  style={{ paddingLeft: 32 }}
                />
              </div>
              <select
                className="form-select"
                value={checklistTypeFilter}
                onChange={e => setChecklistTypeFilter(e.target.value)}
                style={{ width: 'auto', minWidth: 120 }}
              >
                <option value="">All Types</option>
                <option value="PFC">PFC</option>
                <option value="FPT">FPT</option>
                <option value="BEIC">BEIC</option>
                <option value="Integrated">Integrated</option>
                <option value="Seasonal">Seasonal</option>
              </select>
              <select
                className="form-select"
                value={checklistStatusFilter}
                onChange={e => setChecklistStatusFilter(e.target.value)}
                style={{ width: 'auto', minWidth: 140 }}
              >
                <option value="">All Statuses</option>
                <option value="NotStarted">Not Started</option>
                <option value="InProgress">In Progress</option>
                <option value="Complete">Complete</option>
                <option value="NA">N/A</option>
              </select>
              <span className="text-sm text-muted" style={{ whiteSpace: 'nowrap' }}>
                Showing {filteredChecklists.length} of {checklists.length}
              </span>
              {(checklistSearch || checklistStatusFilter || checklistTypeFilter) && (
                <button className="btn btn-ghost btn-sm" onClick={() => { setChecklistSearch(''); setChecklistStatusFilter(''); setChecklistTypeFilter('') }}>
                  <X size={14} /> Clear
                </button>
              )}
            </div>
            {filteredChecklists.length === 0 ? (
              <EmptyState icon={<Search size={48} />} title="No matches" message="No checklists match your search or filter criteria." />
            ) : (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr><th>Name</th><th>Type</th><th>Template</th><th>Status</th><th>Progress</th><th>Assigned To</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {filteredChecklists.map((cl) => (
                    <tr key={cl.id}>
                      <td><Link to={`/checklists/${cl.id}`} className="font-medium">{cl.name}</Link></td>
                      <td><span className="badge badge-primary-light">{cl.type || 'PFC'}</span></td>
                      <td className="text-secondary-color">{cl.templateName}</td>
                      <td><StatusBadge status={cl.status} /></td>
                      <td style={{ minWidth: '120px' }}><ProgressBar value={cl.completionPercentage} size="sm" /></td>
                      <td className="text-secondary-color">{cl.assignedTo || '-'}</td>
                      <td>
                        <div className="table-actions">
                          <Link to={`/checklists/${cl.id}`} className="btn btn-ghost btn-xs">Open</Link>
                          <button className="btn btn-ghost btn-xs btn-icon text-danger" onClick={async () => { await checklistApi.delete(cl.id); loadChecklists() }}><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
            </>
          )}
        </div>
      )}

      {/* Issues Tab */}
      {activeTab === 'issues' && (
        <div>
          <div className="d-flex justify-between align-center mb-4">
            <h3>Issues & Resolutions</h3>
            <div className="d-flex gap-2">
              <Link to="/issues" className="btn btn-secondary btn-sm">
                <FileWarning size={14} /> Open Issues Log
              </Link>
              <button className="btn btn-primary btn-sm" onClick={() => { setEditingIssue(null); setIssueForm({ title: '', description: '', category: 'Design', type: 'Deficiency', priority: 'Medium' }); setIssueResolution(''); setIssuePhotoFiles([]); setIssuePhotos([]); setShowIssueModal(true) }}><Plus size={14} /> Add Issue</button>
            </div>
          </div>
          {issues.length === 0 ? (
            <EmptyState icon={<FileWarning size={48} />} title="No issues" message="No issues have been logged for this project." action={<button className="btn btn-primary" onClick={() => { setEditingIssue(null); setIssueForm({ title: '', description: '', category: 'Design', type: 'Deficiency', priority: 'Medium' }); setIssueResolution(''); setIssuePhotoFiles([]); setIssuePhotos([]); setShowIssueModal(true) }}><Plus size={16} /> Add Issue</button>} />
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr><th>Number</th><th>Title</th><th>Type</th><th>Category</th><th>Priority</th><th>Status</th><th>Phase</th><th style={{ width: '100px' }}>Actions</th></tr>
                </thead>
                <tbody>
                  {issues.map((issue) => (
                    <tr key={issue.id} style={{ cursor: 'pointer' }} onClick={() => openEditIssue(issue)}>
                      <td className="font-medium text-accent">{issue.issueNumber}</td>
                      <td>{issue.title}</td>
                      <td><span className="badge badge-gray-light">{issue.type}</span></td>
                      <td className="text-secondary-color">{issue.category}</td>
                      <td><PriorityBadge priority={issue.priority} /></td>
                      <td onClick={(e) => { e.stopPropagation(); handleQuickIssueStatusChange(issue) }} title="Click to cycle status" style={{ cursor: 'pointer' }}><StatusBadge status={issue.status} /></td>
                      <td className="text-secondary-color">{issue.phase || '-'}</td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="d-flex gap-1">
                          <button className="btn btn-ghost btn-xs btn-icon" title="Edit" onClick={() => openEditIssue(issue)}><Edit2 size={14} /></button>
                          <button className="btn btn-ghost btn-xs btn-icon text-danger" title="Delete" onClick={() => setConfirmDeleteIssue(issue)}><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Submittals Tab */}
      {activeTab === 'submittals' && (
        <div>
          <div className="d-flex justify-between align-center mb-4">
            <h3>Submittals</h3>
            <div className="d-flex gap-2">
              <button className="btn btn-primary btn-sm" onClick={() => { setEditingSubmittal(null); setSubmittalForm({ submittalNumber: `SUB-${String(submittals.length + 1).padStart(3, '0')}`, type: 'Other' }); setShowSubmittalModal(true) }}>
                <Plus size={14} /> Add Submittal
              </button>
              <Link to="/submittals" className="btn btn-secondary btn-sm">
                <Send size={14} /> Open Submittals
              </Link>
            </div>
          </div>
          {submittals.length === 0 ? (
            <EmptyState icon={<Send size={48} />} title="No submittals" message="No submittals have been tracked for this project." action={
              <button className="btn btn-primary btn-sm mt-2" onClick={() => { setEditingSubmittal(null); setSubmittalForm({ submittalNumber: 'SUB-001', type: 'Other' }); setShowSubmittalModal(true) }}>
                <Plus size={14} /> Add Submittal
              </button>
            } />
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr><th>Number</th><th>Title</th><th>Type</th><th>Status</th><th>Due Date</th><th>Reviews</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {submittals.map((sub) => (
                    <tr key={sub.id} style={{ cursor: 'pointer' }} onClick={() => openEditSubmittal(sub)}>
                      <td className="font-medium text-accent">{sub.submittalNumber}</td>
                      <td>{sub.title}</td>
                      <td><span className="badge badge-gray-light">{sub.type.replace(/_/g, ' ')}</span></td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <span style={{ cursor: 'pointer' }} onClick={() => handleQuickSubmittalStatus(sub)} title="Click to cycle status">
                          <StatusBadge status={sub.status} />
                        </span>
                      </td>
                      <td className="text-secondary-color">{sub.dueDate ? new Date(sub.dueDate).toLocaleDateString() : '-'}</td>
                      <td className="text-secondary-color">{sub.reviewCount ?? sub.currentReviewNumber}</td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="d-flex gap-1">
                          <button className="btn btn-ghost btn-xs btn-icon" title="Edit" onClick={() => openEditSubmittal(sub)}><Edit2 size={14} /></button>
                          <button className="btn btn-ghost btn-xs btn-icon text-danger" title="Delete" onClick={() => setConfirmDeleteSubmittal(sub)}><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Documents Tab */}
      {activeTab === 'documents' && (
        <div>
          <div className="d-flex justify-between align-center mb-4">
            <h3>Documents</h3>
          </div>
          <div
            className={`upload-zone${dragOver ? ' drag-over' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFileUpload(e.dataTransfer.files) }}
          >
            <Upload size={32} className="text-muted" />
            <p>Drag and drop files here, or <label className="upload-browse-label"><input type="file" multiple hidden onChange={(e) => handleFileUpload(e.target.files)} /><span className="text-accent cursor-pointer">browse</span></label></p>
          </div>
          {documents.length === 0 ? (
            <EmptyState icon={<FileText size={48} />} title="No documents" message="Upload documents to this project using the area above." />
          ) : (
            <div className="table-container mt-4">
              <table className="data-table">
                <thead>
                  <tr><th>File Name</th><th>Category</th><th>Size</th><th>Uploaded</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {documents.map((doc) => (
                    <tr key={doc.id}>
                      <td className="font-medium"><FileText size={14} className="text-muted" style={{ marginRight: '8px' }} />{doc.fileName}</td>
                      <td className="text-secondary-color">{doc.category || '-'}</td>
                      <td className="text-secondary-color">{(doc.sizeBytes / 1024).toFixed(1)} KB</td>
                      <td className="text-secondary-color">{new Date(doc.uploadedAt).toLocaleDateString()}</td>
                      <td>
                        <div className="table-actions">
                          <button className="btn btn-ghost btn-xs btn-icon text-danger" onClick={() => handleDeleteDocument(doc.id)}><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* System Modal */}
      {showSystemModal && createPortal(
        <div className="modal-overlay" onClick={() => setShowSystemModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingSystemId ? 'Edit System' : 'Add System'}</h2>
              <button className="modal-close" onClick={() => setShowSystemModal(false)}><X size={20} /></button>
            </div>
            <form onSubmit={editingSystemId ? handleUpdateSystem : handleCreateSystem}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group"><label className="required">Code</label><input className="form-input" required value={systemForm.code || ''} onChange={(e) => setSystemForm({ ...systemForm, code: e.target.value })} placeholder="e.g., HVAC-01" /></div>
                  <div className="form-group"><label className="required">Name</label><input className="form-input" required value={systemForm.name || ''} onChange={(e) => setSystemForm({ ...systemForm, name: e.target.value })} placeholder="System name" /></div>
                </div>
                <div className="form-group"><label>Description</label><textarea className="form-textarea" value={systemForm.description || ''} onChange={(e) => setSystemForm({ ...systemForm, description: e.target.value })} rows={3} /></div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowSystemModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : editingSystemId ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>, document.body
      )}

      {/* Equipment Modal */}
      {showEquipModal && createPortal(
        <div className="modal-overlay" onClick={() => setShowEquipModal(false)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingEquipId ? 'Edit Equipment' : 'Add Equipment'}</h2>
              <button className="modal-close" onClick={() => setShowEquipModal(false)}><X size={20} /></button>
            </div>
            <form onSubmit={editingEquipId ? handleUpdateEquipment : handleCreateEquipment}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group"><label className="required">Tag</label><input className="form-input" required value={equipForm.tag || ''} onChange={(e) => setEquipForm({ ...equipForm, tag: e.target.value })} placeholder="e.g., AHU-01" /></div>
                  <div className="form-group"><label className="required">Name</label><input className="form-input" required value={equipForm.name || ''} onChange={(e) => setEquipForm({ ...equipForm, name: e.target.value })} placeholder="Equipment name" /></div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label>Manufacturer</label><input className="form-input" value={equipForm.manufacturer || ''} onChange={(e) => setEquipForm({ ...equipForm, manufacturer: e.target.value })} /></div>
                  <div className="form-group"><label>Model</label><input className="form-input" value={equipForm.model || ''} onChange={(e) => setEquipForm({ ...equipForm, model: e.target.value })} /></div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label>Serial Number</label><input className="form-input" value={equipForm.serialNumber || ''} onChange={(e) => setEquipForm({ ...equipForm, serialNumber: e.target.value })} /></div>
                  <div className="form-group"><label>Location</label><input className="form-input" value={equipForm.location || ''} onChange={(e) => setEquipForm({ ...equipForm, location: e.target.value })} /></div>
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select className="form-select" value={equipForm.status || 'Pending'} onChange={(e) => setEquipForm({ ...equipForm, status: e.target.value as EquipmentStatus })}>
                    <option value="Pending">Pending</option>
                    <option value="Installed">Installed</option>
                    <option value="Tested">Tested</option>
                    <option value="Commissioned">Commissioned</option>
                    <option value="Deficient">Deficient</option>
                  </select>
                </div>
                <div className="form-group"><label>Description</label><textarea className="form-textarea" value={equipForm.description || ''} onChange={(e) => setEquipForm({ ...equipForm, description: e.target.value })} rows={2} /></div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEquipModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : editingEquipId ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>, document.body
      )}

      {/* Equipment Import Dialog */}
      <CsvImportDialog
        isOpen={showEquipImport}
        onClose={() => setShowEquipImport(false)}
        title={`Import Equipment to ${selectedSystem?.code || 'System'}`}
        fields={equipImportFields}
        onImport={handleEquipImport}
      />

      {/* Checklist Modal */}
      {showChecklistModal && createPortal(
        <div className="modal-overlay" onClick={() => setShowChecklistModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create Checklist{bulkMode ? 's (Bulk)' : ''}</h2>
              <button className="modal-close" onClick={() => setShowChecklistModal(false)}><X size={20} /></button>
            </div>
            <form onSubmit={handleCreateChecklist}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="required">Template</label>
                  <select className="form-select" required value={checklistForm.templateId} onChange={(e) => setChecklistForm({ ...checklistForm, templateId: e.target.value })}>
                    <option value="">Select a template...</option>
                    {templates.map((t) => (<option key={t.id} value={t.id}>{t.name}</option>))}
                  </select>
                </div>
                <div className="form-group">
                  <label>System</label>
                  <select className="form-select" value={checklistForm.systemId} onChange={async (e) => {
                    const sysId = e.target.value
                    setChecklistForm({ ...checklistForm, systemId: sysId, equipmentId: '' })
                    setBulkEquipIds(new Set())
                    if (sysId) {
                      try {
                        const equips = await equipmentApi.getBySystem(sysId)
                        setChecklistEquipOptions(equips)
                      } catch { setChecklistEquipOptions([]) }
                    } else {
                      setChecklistEquipOptions([])
                    }
                  }}>
                    <option value="">All systems</option>
                    {allSystems.map((s) => (<option key={s.id} value={s.id}>{s.code} - {s.name}</option>))}
                  </select>
                </div>
                {checklistEquipOptions.length > 0 && (
                  <div className="form-group">
                    <div className="d-flex justify-between align-center mb-2">
                      <label style={{ margin: 0 }}>Equipment</label>
                      <label className="d-flex align-center gap-2" style={{ fontSize: '0.85rem', cursor: 'pointer' }}>
                        <input type="checkbox" checked={bulkMode} onChange={(e) => {
                          setBulkMode(e.target.checked)
                          if (!e.target.checked) {
                            setBulkEquipIds(new Set())
                          }
                        }} />
                        Bulk (multiple)
                      </label>
                    </div>
                    {bulkMode ? (
                      <div style={{ border: '1px solid var(--ise-border)', borderRadius: '6px', maxHeight: '200px', overflowY: 'auto' }}>
                        <div style={{ padding: '6px 10px', borderBottom: '1px solid var(--ise-border)' }}>
                          <label className="d-flex align-center gap-2" style={{ fontSize: '0.85rem', cursor: 'pointer', color: 'var(--ise-text-secondary)' }}>
                            <input type="checkbox"
                              checked={bulkEquipIds.size === checklistEquipOptions.length && checklistEquipOptions.length > 0}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setBulkEquipIds(new Set(checklistEquipOptions.map(eq => eq.id)))
                                } else {
                                  setBulkEquipIds(new Set())
                                }
                              }}
                            />
                            Select all ({checklistEquipOptions.length})
                          </label>
                        </div>
                        {checklistEquipOptions.map((eq) => (
                          <div key={eq.id} style={{ padding: '4px 10px', borderBottom: '1px solid var(--ise-border-light, transparent)' }}>
                            <label className="d-flex align-center gap-2" style={{ fontSize: '0.85rem', cursor: 'pointer' }}>
                              <input type="checkbox" checked={bulkEquipIds.has(eq.id)} onChange={(e) => {
                                const next = new Set(bulkEquipIds)
                                if (e.target.checked) next.add(eq.id); else next.delete(eq.id)
                                setBulkEquipIds(next)
                              }} />
                              {eq.tag} - {eq.name}
                            </label>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <select className="form-select" value={checklistForm.equipmentId} onChange={(e) => setChecklistForm({ ...checklistForm, equipmentId: e.target.value })}>
                        <option value="">No specific equipment</option>
                        {checklistEquipOptions.map((eq) => (<option key={eq.id} value={eq.id}>{eq.tag} - {eq.name}</option>))}
                      </select>
                    )}
                  </div>
                )}
                <div className="form-group"><label>Assigned To</label><input className="form-input" value={checklistForm.assignedTo} onChange={(e) => setChecklistForm({ ...checklistForm, assignedTo: e.target.value })} placeholder="Assignee name" /></div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowChecklistModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving || (bulkMode && bulkEquipIds.size === 0)}>
                  {saving ? 'Creating...' : bulkMode ? `Create ${bulkEquipIds.size} Checklist${bulkEquipIds.size !== 1 ? 's' : ''}` : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>, document.body
      )}

      {/* Issue Modal */}
      {showIssueModal && createPortal(
        <div className="modal-overlay" onClick={() => { setShowIssueModal(false); setEditingIssue(null) }}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingIssue ? 'Edit Issue' : 'Add Issue'}</h2>
              <button className="modal-close" onClick={() => { setShowIssueModal(false); setEditingIssue(null) }}><X size={20} /></button>
            </div>
            <form onSubmit={handleCreateIssue}>
              <div className="modal-body">
                <div className="form-group"><label className="required">Title</label><input className="form-input" required value={issueForm.title || ''} onChange={(e) => setIssueForm({ ...issueForm, title: e.target.value })} /></div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Type</label>
                    <select className="form-select" value={issueForm.type || 'Deficiency'} onChange={(e) => setIssueForm({ ...issueForm, type: e.target.value as IssueType })}>
                      <option value="Deficiency">Deficiency</option>
                      <option value="RFI">RFI</option>
                      <option value="Observation">Observation</option>
                      <option value="NonConformance">Non-Conformance</option>
                      <option value="ChangeOrder">Change Order</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Category</label>
                    <select className="form-select" value={issueForm.category || 'Design'} onChange={(e) => setIssueForm({ ...issueForm, category: e.target.value as IssueCategory })}>
                      <option value="Design">Design</option>
                      <option value="Installation">Installation</option>
                      <option value="Controls">Controls</option>
                      <option value="TAB">TAB</option>
                      <option value="Commissioning">Commissioning</option>
                      <option value="Safety">Safety</option>
                      <option value="Documentation">Documentation</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Priority</label>
                    <select className="form-select" value={issueForm.priority || 'Medium'} onChange={(e) => setIssueForm({ ...issueForm, priority: e.target.value as PunchItemPriority })}>
                      <option value="Critical">Critical</option>
                      <option value="High">High</option>
                      <option value="Medium">Medium</option>
                      <option value="Low">Low</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  {editingIssue && (
                    <div className="form-group">
                      <label>Status</label>
                      <select className="form-select" value={issueStatus} onChange={(e) => setIssueStatus(e.target.value as IssueStatus)}>
                        <option value="Open">Open</option>
                        <option value="InProgress">In Progress</option>
                        <option value="PendingResponse">Pending Response</option>
                        <option value="Resolved">Resolved</option>
                        <option value="Closed">Closed</option>
                        <option value="Deferred">Deferred</option>
                      </select>
                    </div>
                  )}
                  <div className="form-group">
                    <label>Phase</label>
                    <select className="form-select" value={issueForm.phase || ''} onChange={(e) => setIssueForm({ ...issueForm, phase: (e.target.value || undefined) as IssuePhase | undefined })}>
                      <option value="">-- Select Phase --</option>
                      <option value="Design">Design</option>
                      <option value="PFAT">PFAT</option>
                      <option value="FPT">FPT</option>
                      <option value="Training">Training</option>
                      <option value="Closeout">Closeout</option>
                      <option value="Warranty">Warranty</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label>Responsible Party</label><input className="form-input" value={issueForm.responsibleParty || ''} onChange={(e) => setIssueForm({ ...issueForm, responsibleParty: e.target.value })} /></div>
                  <div className="form-group"><label>Assigned To</label><input className="form-input" value={issueForm.assignedTo || ''} onChange={(e) => setIssueForm({ ...issueForm, assignedTo: e.target.value })} /></div>
                </div>
                <div className="form-group"><label>Due Date</label><input type="date" className="form-input" value={issueForm.dueDate || ''} onChange={(e) => setIssueForm({ ...issueForm, dueDate: e.target.value || undefined })} /></div>
                <div className="form-group"><label>Description</label><textarea className="form-textarea" value={issueForm.description || ''} onChange={(e) => setIssueForm({ ...issueForm, description: e.target.value })} rows={3} /></div>
                {editingIssue && (
                  <div className="form-group"><label>Resolution</label><textarea className="form-textarea" value={issueResolution} onChange={(e) => setIssueResolution(e.target.value)} rows={3} placeholder="Describe how the issue was resolved..." /></div>
                )}
                <div className="form-group">
                  <label>Photos</label>
                  <div className="d-flex align-center gap-3 mb-3">
                    <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
                      <Camera size={14} /> Add Photo{issuePhotoFiles.length > 0 ? 's' : ''}
                      <input type="file" accept="image/*" multiple hidden onChange={(e) => {
                        if (e.target.files) setIssuePhotoFiles(prev => [...prev, ...Array.from(e.target.files!)])
                        e.target.value = ''
                      }} />
                    </label>
                    {issuePhotoFiles.length > 0 && <span className="text-sm text-secondary-color">{issuePhotoFiles.length} file{issuePhotoFiles.length !== 1 ? 's' : ''} staged</span>}
                  </div>
                  {issuePhotoFiles.length > 0 && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '8px', marginBottom: issuePhotos.length > 0 ? '12px' : 0 }}>
                      {issuePhotoFiles.map((file, idx) => (
                        <div key={idx} style={{ position: 'relative', borderRadius: 'var(--radius-md)', overflow: 'hidden', border: '1px solid var(--accent-green)', aspectRatio: '1' }}>
                          <img src={URL.createObjectURL(file)} alt={file.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          <div style={{ position: 'absolute', top: '4px', right: '4px', display: 'flex', gap: '2px' }}>
                            <span style={{ background: 'rgba(16,185,129,0.85)', color: 'white', fontSize: '10px', padding: '2px 6px', borderRadius: '4px' }}>New</span>
                            <button type="button" onClick={() => setIssuePhotoFiles(prev => prev.filter((_, i) => i !== idx))} style={{ background: 'rgba(220,38,38,0.85)', color: 'white', border: 'none', borderRadius: '4px', padding: '2px 6px', cursor: 'pointer', fontSize: '10px' }}><X size={10} /></button>
                          </div>
                          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.7)', color: 'white', fontSize: '10px', padding: '2px 6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {issuePhotosLoading && <p className="text-sm text-muted">Loading photos...</p>}
                  {issuePhotos.length > 0 && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '8px' }}>
                      {issuePhotos.map((photo) => (
                        <div key={photo.id} style={{ position: 'relative', borderRadius: 'var(--radius-md)', overflow: 'hidden', border: '1px solid var(--border)', aspectRatio: '1' }}>
                          <img src={issueApi.getPhotoUrl(String(editingIssue!.id), photo.id)} alt={photo.fileName} style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }} onClick={() => setIssueLightboxPhoto(photo)} />
                          <div style={{ position: 'absolute', top: '4px', right: '4px', display: 'flex', gap: '2px' }}>
                            <span style={{ background: 'rgba(0,0,0,0.7)', color: 'white', fontSize: '10px', padding: '2px 6px', borderRadius: '4px' }}>{photo.photoType}</span>
                            <button type="button" onClick={(e) => { e.stopPropagation(); handleDeleteIssuePhoto(photo.id) }} style={{ background: 'rgba(220,38,38,0.85)', color: 'white', border: 'none', borderRadius: '4px', padding: '2px 6px', cursor: 'pointer', fontSize: '10px' }}><Trash2 size={10} /></button>
                          </div>
                          {photo.caption && (
                            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.7)', color: 'white', fontSize: '10px', padding: '2px 6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{photo.caption}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => { setShowIssueModal(false); setEditingIssue(null) }}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? (editingIssue ? 'Saving...' : 'Creating...') : (editingIssue ? 'Save Changes' : 'Create')}</button>
              </div>
            </form>
          </div>
        </div>, document.body
      )}

      {issueLightboxPhoto && editingIssue && createPortal(
        <div className="modal-overlay" onClick={() => setIssueLightboxPhoto(null)} style={{ zIndex: 2000 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: '90vw', maxHeight: '90vh', position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <img src={issueApi.getPhotoUrl(String(editingIssue.id), issueLightboxPhoto.id)} alt={issueLightboxPhoto.fileName} style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: 'var(--radius-lg)', objectFit: 'contain' }} />
            <div style={{ marginTop: '12px', color: 'white', textAlign: 'center', fontSize: 'var(--font-size-sm)', textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}>
              <div className="font-medium">{issueLightboxPhoto.fileName}</div>
              <div className="text-sm" style={{ opacity: 0.8 }}>
                {issueLightboxPhoto.photoType} {issueLightboxPhoto.caption ? `— ${issueLightboxPhoto.caption}` : ''}
                {' • '}{new Date(issueLightboxPhoto.createdAt).toLocaleString()}
              </div>
            </div>
            <button onClick={() => setIssueLightboxPhoto(null)} style={{ position: 'absolute', top: '-10px', right: '-10px', background: 'rgba(0,0,0,0.7)', color: 'white', border: 'none', borderRadius: '50%', width: '32px', height: '32px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><X size={18} /></button>
          </div>
        </div>, document.body
      )}

      <ConfirmDialog
        isOpen={!!confirmDeleteIssue}
        title="Delete Issue"
        message={`Are you sure you want to delete "${confirmDeleteIssue?.title}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleDeleteIssue}
        onCancel={() => setConfirmDeleteIssue(null)}
      />

      {/* Submittal Modal */}
      {showSubmittalModal && createPortal(
        <div className="modal-overlay" onClick={() => { setShowSubmittalModal(false); setEditingSubmittal(null) }}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingSubmittal ? 'Edit Submittal' : 'Add Submittal'}</h2>
              <button className="modal-close" onClick={() => { setShowSubmittalModal(false); setEditingSubmittal(null) }}><X size={20} /></button>
            </div>
            <form onSubmit={handleCreateSubmittal}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label>Submittal Number</label>
                    <input className="form-input" value={submittalForm.submittalNumber || ''} readOnly={!!editingSubmittal} onChange={(e) => setSubmittalForm({ ...submittalForm, submittalNumber: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="required">Title</label>
                    <input className="form-input" required value={submittalForm.title || ''} onChange={(e) => setSubmittalForm({ ...submittalForm, title: e.target.value })} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Type</label>
                    <select className="form-select" value={submittalForm.type || 'Other'} onChange={(e) => setSubmittalForm({ ...submittalForm, type: e.target.value as SubmittalType })}>
                      {(['TAB_Report', 'Controls_Checkout', 'Design_Review', 'Shop_Drawing', 'O_M_Manual', 'Training_Doc', 'Other'] as SubmittalType[]).map((t) => (
                        <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
                      ))}
                    </select>
                  </div>
                  {editingSubmittal && (
                    <div className="form-group">
                      <label>Status</label>
                      <select className="form-select" value={(submittalForm as any).status || 'Pending'} onChange={(e) => setSubmittalForm({ ...submittalForm, status: e.target.value as any } as any)}>
                        {(['Pending', 'Under_Review', 'Approved', 'Approved_As_Noted', 'Rejected', 'Resubmit'] as SubmittalStatus[]).map((s) => (
                          <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Submitted By</label>
                    <input className="form-input" value={submittalForm.submittedBy || ''} onChange={(e) => setSubmittalForm({ ...submittalForm, submittedBy: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Due Date</label>
                    <input type="date" className="form-input" value={submittalForm.dueDate || ''} onChange={(e) => setSubmittalForm({ ...submittalForm, dueDate: e.target.value || undefined })} />
                  </div>
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea className="form-textarea" value={submittalForm.description || ''} onChange={(e) => setSubmittalForm({ ...submittalForm, description: e.target.value })} rows={3} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => { setShowSubmittalModal(false); setEditingSubmittal(null) }}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? (editingSubmittal ? 'Saving...' : 'Creating...') : (editingSubmittal ? 'Save Changes' : 'Create')}</button>
              </div>
            </form>
          </div>
        </div>, document.body
      )}

      <ConfirmDialog
        isOpen={!!confirmDeleteSubmittal}
        title="Delete Submittal"
        message={`Are you sure you want to delete "${confirmDeleteSubmittal?.title}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleDeleteSubmittal}
        onCancel={() => setConfirmDeleteSubmittal(null)}
      />

      <ConfirmDialog
        isOpen={confirmDelete}
        title="Delete Project"
        message={`Are you sure you want to delete "${project.name}"? This will also delete all systems, equipment, checklists, and issues associated with this project.`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleDeleteProject}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  )
}
